/* oxlint-disable no-console -- structured JSON console telemetry */
import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';

const noopTrace = { end: () => {}, fail: () => {} };

/** Console JSON logs for dev and optional `TELEMETRY_PROVIDER=console`. */
export class ConsoleTelemetryAdapter implements TelemetryPort {
  private user: { id?: string; email?: string; role?: string } | null = null;

  private envelope(correlationId: string | undefined, fields: Record<string, unknown>): Record<string, unknown> {
    return {
      timestamp: new Date().toISOString(),
      correlationId: correlationId ?? undefined,
      user: this.user,
      pageUrl: globalThis.location?.href,
      ...fields,
    };
  }

  logInfo(message: string, context?: Record<string, unknown>): void {
    const cid = typeof context?.correlationId === 'string' ? context.correlationId : undefined;
    const { correlationId: _c, ...rest } = context ?? {};
    console.info('[Observability:info]', JSON.stringify(this.envelope(cid, { message, ...rest })));
  }

  logError(error: Error | string, context?: Record<string, unknown>): void {
    const cid = typeof context?.correlationId === 'string' ? context.correlationId : undefined;
    const { correlationId: _c, ...rest } = context ?? {};
    const err =
      error instanceof Error
        ? { errorName: error.name, errorMessage: error.message }
        : { errorMessage: String(error) };
    const payload = this.envelope(cid, { ...err, ...rest });
    if (error instanceof Error) {
      console.error('[Observability:error]', JSON.stringify(payload), '\n', error);
    } else {
      console.error('[Observability:error]', JSON.stringify(payload));
    }
  }

  trackEvent(eventName: string, payload?: Record<string, unknown>): void {
    console.debug('[Observability:event]', JSON.stringify(this.envelope(undefined, { eventName, payload })));
  }

  setUserContext(user: { id: string; role?: string; [key: string]: unknown } | null): void {
    this.user = user
      ? { id: user.id, email: user.email as string | undefined, role: user.role }
      : null;
  }

  logHttpRequest(meta: { correlationId: string; method: string; url: string }): void {
    console.info(
      '[Observability:http:request]',
      JSON.stringify(this.envelope(meta.correlationId, { type: 'http.request', ...meta })),
    );
  }

  logHttpResponse(meta: { correlationId: string; status: number; durationMs: number; url: string }): void {
    console.info(
      '[Observability:http:response]',
      JSON.stringify(this.envelope(meta.correlationId, { type: 'http.response', ...meta })),
    );
  }

  startTrace(): { end: () => void; fail: (err: Error) => void } {
    return noopTrace;
  }
}
